import { yupResolver } from '@hookform/resolvers/yup';
import { ValidationState } from '@sparemin/blockhead';
import {
  FieldValues,
  useForm,
  UseFormProps,
  UseFormReturn,
} from 'react-hook-form';
import { AnyObjectSchema } from 'yup';
import Lazy from 'yup/lib/Lazy';
import { getValidationState } from 'utils/form';

interface UnregisteredErrorFieldValue {
  unregisteredError?: never;
}

type UseAuthFormFieldValues<TFieldValues extends FieldValues> = TFieldValues &
  UnregisteredErrorFieldValue;

interface SchemaProps<TSchema extends AnyObjectSchema | Lazy<never>> {
  schema: TSchema;
  schemaOptions?: Parameters<TSchema['validate']>[1];
}

type UseAuthFormProps<
  TFieldValues extends FieldValues,
  TSchema extends AnyObjectSchema | Lazy<never>,
  TContext = never,
> = Omit<
  UseFormProps<UseAuthFormFieldValues<TFieldValues>, TContext>,
  'resolver'
> &
  SchemaProps<TSchema>;

export type AuthFormValidationStatus<TFieldValues extends FieldValues> = Record<
  keyof TFieldValues,
  ValidationState
>;

export interface UseAuthFormResult<
  TFieldValues extends FieldValues,
  TContext = never,
> extends UseFormReturn<UseAuthFormFieldValues<TFieldValues>, TContext> {
  hasInputError: boolean;
  validationState: AuthFormValidationStatus<TFieldValues>;
}

export default function useAuthForm<
  TFieldValues extends FieldValues,
  TSchema extends AnyObjectSchema | Lazy<never> = AnyObjectSchema,
  TContext = never,
>(
  props: UseAuthFormProps<TFieldValues, TSchema, TContext>,
): UseAuthFormResult<TFieldValues, TContext> {
  const { schema, schemaOptions, ...restProps } = props;
  const result = useForm<UseAuthFormFieldValues<TFieldValues>, TContext>({
    ...restProps,
    resolver: yupResolver(schema, schemaOptions),
  });

  const {
    formState: { errors },
  } = result;

  // unregistered errors shouldn't prevent form submissions.  they might be transient
  // api errors that resolve themselves by simply clicking submit again
  const hasInputError =
    Object.keys(errors).filter((key) => key !== 'unregisteredError').length > 0;

  const validationState = Object.keys(result.getValues()).reduce(
    (acc, key: keyof UseAuthFormFieldValues<TFieldValues>) => {
      acc[key] = getValidationState(errors, key);
      return acc;
    },
    {} as AuthFormValidationStatus<TFieldValues>,
  );

  return {
    ...result,
    hasInputError,
    validationState,
  };
}
